import React from 'react';
import { bool, object, string, arrayOf, shape, number } from 'prop-types';
import { IntlProvider, FormattedMessage } from '@godaddy/react-mintl';
import LinkedIn from '@ux/icon/linkedin';
import Facebook from '@ux/icon/facebook';
import Twitter from '@ux/icon/twitter';
import Social from '@ux/icon/social';
import Link from '@ux/link';
import CurrencySelector from '../currency-selector';
import golfToEid from '@gd-internal/golf-to-eid';

import '@ux/icon/linkedin/styles';
import '@ux/icon/facebook/styles';
import '@ux/icon/twitter/styles';
import '@ux/icon/social/styles';
import '../currency-selector/styles.css';

const eidPrefix = 'uxp.hyd.reseller_sales_footer';

const footerLinkEid = {
  prefix: eidPrefix,
  postfix: 'link.click'
};

const utosLinkId = 'utos';

export const RESELLER_TYPE_API = 3;
export const PRIVATE_LABEL_123REG = 587240;

/**
 * Mapping of icon names to their Icon components.
 *
 * @type {Object}
 * @private
 */
const Icons = {
  linkedin: LinkedIn,
  facebook: Facebook,
  twitter: Twitter
};

/**
 * PropType shape for a single social media link
 */
const socialMediaLinkShape = shape({
  href: string,
  title: string,
  id: string,
  icon: string
});

/**
 * Helper component for social media links
 *
 * @returns {ReactElement} The social media link item
 */
const SocialMediaLinkItem = ({ link }) => {
  const Icon = Icons[link.icon] || Social;

  return (
    <li>
      {link.id && link.title && (
        <Link
          href={ link.href }
          data-eid={ golfToEid(link.id, footerLinkEid) }
          target='_blank'
          aria-label={ link.title }
        >
          <Icon />
        </Link>
      )}
    </li>
  );
};

SocialMediaLinkItem.propTypes = {
  link: socialMediaLinkShape.isRequired
};

/**
 * Build a link element with EID tracking
 *
 * @param {Object} link The link object with href, title, and id
 * @returns {ReactElement} Link element with EID tracking
 */
const buildLink = (link) => {
  return (
    <Link
      href={ link.href }
      data-eid={ golfToEid(link.id, footerLinkEid) }>
      {link.title}
    </Link>
  );
};

/**
 * Prepare UTOS link data with EID
 *
 * @param {Array} legal Legal links array
 * @returns {Object|null} UTOS link with EID or null
 */
const prepareUtosLink = (legal) => {
  const utosLink = legal.filter(link => link.id === utosLinkId)[0];
  return utosLink ? { ...utosLink, eid: golfToEid(utosLink.id, footerLinkEid) } : null;
};

/**
 * Check if currency selector should be displayed
 *
 * @param {number} resellerTypeId Reseller type ID
 * @param {boolean} multiCurrencyPricingEnabled MCP feature flag
 * @param {number} privateLabelId Private label ID
 * @returns {boolean} True if currency selector should display
 */
const shouldShowCurrencySelector = (resellerTypeId, multiCurrencyPricingEnabled, privateLabelId) => {
  const isApiReseller = resellerTypeId === RESELLER_TYPE_API;
  const is123Reg = privateLabelId === PRIVATE_LABEL_123REG;
  return !isApiReseller && multiCurrencyPricingEnabled && !is123Reg;
};

/**
 * Render and expose the Reseller Sales Footer as a functional component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Reseller Footer
 * @api public
 */
const ResellerSalesFooter = (props) => {
  const {
    footer,
    legal,
    socialMedia,
    market,
    messages,
    resellerTypeId,
    multiCurrencyPricingEnabled,
    privateLabelId,
    currency,
    urls,
    urlArgs,
    features = {}
  } = props;

  const links = footer;
  const legalLinks = legal.filter(link => link.id !== utosLinkId);
  const utosLinkValues = prepareUtosLink(legal);

  // Filter social media links to only those with href
  const validSocialMediaLinks = socialMedia?.filter(link => link && link.href) || [];

  const hasLinks = links && links.length > 0;
  const hasSocialMediaLinks = validSocialMediaLinks.length > 0;
  const hasLegalLinks = legalLinks && legalLinks.length > 0;
  const showCurrencySelector = shouldShowCurrencySelector(
    resellerTypeId,
    multiCurrencyPricingEnabled,
    privateLabelId
  );

  const termsOfService = (
    <a href={ utosLinkValues?.href } data-eid={ utosLinkValues?.eid }>
      {utosLinkValues?.title}
    </a>
  );

  const currencySelectorProps = {
    eidPrefix: footerLinkEid.prefix,
    market,
    currency,
    urls,
    privateLabelId,
    isReseller: true,
    messages
  };

  const doNotSellId = 'Sales:Footer:Privacy:DoNotSell';

  return (
    <IntlProvider locale={ market } messages={ messages }>
      <div className='reseller-footer' role='contentinfo'>
        <div className='reseller-container'>
          {hasLinks && (
            <ul className='reseller-footer-links'>
              {links.map((link) => (
                <li key={ link.id }>{buildLink(link)}</li>
              ))}
            </ul>
          )}
          {showCurrencySelector && (
            <div className='currency-selector-wrapper'>
              <CurrencySelector { ...currencySelectorProps } />
            </div>
          )}
          {hasSocialMediaLinks && (
            <ul className='reseller-footer-social-media-links'>
              {validSocialMediaLinks.map(socialMediaLink => (
                <SocialMediaLinkItem key={ socialMediaLink.id } link={ socialMediaLink } />
              ))}
            </ul>
          )}
          {utosLinkValues && (
            <p className='reseller-footer-utos'>
              <FormattedMessage
                id='SalesReseller:Footer:UToS:Simple'
                values={{ termsOfService }}
              />
            </p>
          )}
          {hasLegalLinks && (
            <ul className='reseller-footer-legal'>
              {legalLinks.map((link) => (
                <li key={ link.id }>{buildLink(link)}</li>
              ))}
              {features.doNotSellLink && urls.cookies?.href && (
                <li className='reseller-footer-legal'>
                  <Link
                    className='reseller-footer-do-not-sell'
                    urlArgs={ urlArgs }
                    href={ urls.cookies.href }
                    data-eid={ golfToEid(doNotSellId, footerLinkEid) }>
                    <FormattedMessage id={ doNotSellId } />
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </IntlProvider>
  );
};

const linksPropType = arrayOf(
  shape({
    href: string.isRequired,
    title: string.isRequired,
    id: string.isRequired
  })
).isRequired;

const socialMediaPropType = arrayOf(socialMediaLinkShape);

ResellerSalesFooter.propTypes = {
  messages: object.isRequired,
  market: string.isRequired,
  resellerTypeId: number.isRequired,
  features: object,
  footer: linksPropType,
  legal: linksPropType,
  socialMedia: socialMediaPropType,
  multiCurrencyPricingEnabled: bool,
  currency: string,
  urls: object,
  urlArgs: object,
  privateLabelId: number.isRequired
};

export default ResellerSalesFooter;
